'use client';
import { useEffect, useState } from 'react';

import useSoundsPreloadStore from 'state/soundsPreloadStore';
import { publicAssets } from 'utils/gcs';
import { logger } from 'utils/logger';

const useInitialSoundsPreload = () => {
  const [progress, setProgress] = useState(0);
  const soundLibrary = useSoundsPreloadStore(s => s.library);
  const initialPreloaded = useSoundsPreloadStore(s => s.initialPreloaded);
  const setInitialPreloaded = useSoundsPreloadStore(s => s.setInitialPreloaded);

  const fetchSfxFiles = async (): Promise<{ [key: string]: string }> => {
    const response = await fetch(publicAssets('/sfx/initial_sfx.json'));
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      return {};
    }
  };

  const loadFilesAsBlob = async (
    filePaths: { [key: string]: string },
    amount: number
  ): Promise<{ [key: string]: string }> => {
    const loadedFiles: { [key: string]: string } = {};

    let index = 0;
    for (const key of Object.keys(filePaths)) {
      const response = await fetch(publicAssets(filePaths[key]));

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        loadedFiles[key] = url;
      } else {
        loadedFiles[key] = publicAssets(filePaths[key]);
      }

      const completedFiles = index + 1;
      const progress = Math.round((completedFiles / amount) * 100);
      setProgress(progress);
      index++;
    }

    return loadedFiles;
  };

  useEffect(() => {
    if (initialPreloaded) {
      return;
    }

    const preload = async () => {
      try {
        const files = await fetchSfxFiles();
        const totalFiles = Object.keys(files).length;
        const loaded = await loadFilesAsBlob(files, totalFiles);
        Object.keys(loaded).forEach(key => {
          soundLibrary[key] = { src: loaded[key] };
        });
      } catch (err) {
        logger.error('Error loading sound effects.', err);
      } finally {
        setInitialPreloaded(true);
        setProgress(100);
      }
    };

    preload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPreloaded, setInitialPreloaded]);

  return { progress };
};

export default useInitialSoundsPreload;
